import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import ConfirmationModal from "../components/ConfirmationModal";

const AlertContext = createContext(null);

export function useAlert() {
  const ctx = useContext(AlertContext);
  if (!ctx) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return ctx;
}

export function AlertProvider({ children }) {
  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    onClose: null,
  });

  const sanitizeAlertMessage = useCallback((value) => {
    const raw = value != null ? String(value) : "";
    // Avoid surfacing raw server origin/URLs in user-facing messages (common accidental UX leak).
    return raw.replace(/https?:\/\/[^\s)]+/g, "").replace(/\s{2,}/g, " ").trim();
  }, []);

  const showAlert = useCallback((message, title = "Message", onClose) => {
    setDialog({
      open: true,
      title,
      message: sanitizeAlertMessage(message),
      onClose: typeof onClose === "function" ? onClose : null,
    });
  }, [sanitizeAlertMessage]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const originalAlert = window.alert;
    window.alert = (msg) => {
      showAlert(msg);
    };
    return () => {
      window.alert = originalAlert;
    };
  }, [showAlert]);

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      <ConfirmationModal
        open={dialog.open}
        title={dialog.title || "Message"}
        message={dialog.message}
        mode="alert"
        onConfirm={() => {
          const fn = dialog.onClose;
          setDialog({ open: false, title: "", message: "", onClose: null });
          if (fn) fn();
        }}
        onCancel={() =>
          setDialog({ open: false, title: "", message: "", onClose: null })
        }
      />
    </AlertContext.Provider>
  );
}

